// X72 (Grant 2026-10-20, the admin calendar): "as an admin I need to be able
// to block out times ad-hoc that are for matches/free play/bookable that
// override the defaults of the group for a particular day." The pure half of
// the day changes: the kinds and their words, where a change sits on the
// calendar's row grid, the dialog's time choices, the request body and the
// lines the Bookings page and the legend read. No DOM, no network.

import {clockInZone, clockWord, dayInZone, gridRowAt, instantInZone, minutesOf, toHhmm} from './bookings.js';

export const OVERRIDE_KIND = {bookingNeeded: 'BookingNeeded', matchesOnly: 'MatchesOnly', openPlay: 'OpenPlay', closed: 'Closed'};

/** The four kinds in the dialog's order, each with the sentence its tooltip and the legend dialog read. */
export const OVERRIDE_KINDS = Object.freeze([
  {value: OVERRIDE_KIND.bookingNeeded, title: 'Booking needed', cls: 'free', icon: null, sentence: 'Booking needed: players must book these times on Tap Leagues.'},
  {value: OVERRIDE_KIND.matchesOnly, title: 'Matches only', cls: 'matchesonly', icon: 'mdi-whistle', sentence: 'Matches only: only league, ladder and other competition matches can be booked.'},
  {value: OVERRIDE_KIND.openPlay, title: 'Open play', cls: 'open', icon: 'mdi-walk', sentence: 'Open play: no booking needed. First come, first served.'},
  {value: OVERRIDE_KIND.closed, title: 'Closed', cls: 'cant', icon: 'mdi-cancel', sentence: 'Closed: nobody can play or book.'},
]);

export function overrideKind(kind) {
  return OVERRIDE_KINDS.find(k => k.value === kind) ?? OVERRIDE_KINDS[0];
}

export function overrideKindWord(kind) {
  return overrideKind(kind).title;
}

/** X91: a change preview that touches a booking (cancelled, moved or warned), so the change is confirmed in its dialog. */
// X234: a change that reaches a club block asks too (keep it or cancel it), even when it is kept.
export const previewTouches = (p) => !!p && ((p.cancelled ?? 0) + (p.moved ?? 0) + (p.warned ?? 0) + (p.clubBlocks ?? 0)) > 0;

/** The legend entry for a changed band; its swatch is drawn by the calendar (a mini band), not a booking cell. */
export const CHANGED_LEGEND = Object.freeze({key: 'changed', cls: 'changed', word: 'Changed for this day', sentence: 'Changed for this day: the hours an admin set for this day alone, instead of the usual booking hours. Click a time in it to change it.'});

/** "6pm", "6:30pm", "midnight" from "HH:mm" (or "24:00"). */
export function hhmmWord(hhmm) {
  if (!hhmm) return '';
  if (hhmm === '24:00' || hhmm === '00:00') return 'midnight';
  const m = minutesOf(hhmm);
  const h = Math.floor(m / 60);
  const mm = m % 60;
  const h12 = h % 12 === 0 ? 12 : h % 12;
  const suffix = h < 12 ? 'am' : 'pm';
  return mm ? `${h12}:${String(mm).padStart(2, '0')}${suffix}` : `${h12}${suffix}`;
}

/** "Matches only 6pm–10pm" (the API's own line when it has one). */
export function overrideLine(o) {
  if (!o) return '';
  if (o.line) return o.line;
  return `${overrideKindWord(o.kind)} ${hhmmWord(o.startLocal)}–${hhmmWord(o.endLocal)}`;
}

/** X94 (R35 L2): a change in words on the grid's 24-hour clock, "Matches only 18:00–19:00" (never the API's 12-hour line). */
export function overrideLine24(o) {
  if (!o) return '';
  return `${overrideKindWord(o.kind)} ${clockWord(o.startLocal)}–${clockWord(o.endLocal, {end: true})}`;
}

/**
 * Where a change sits on the calendar's row grid: {row, span} clamped to the
 * rows on screen, or null when none of it is on the grid (an open-play change
 * that took the whole day away leaves no rows at all; the strip above the grid
 * still lists it).
 */
export function overrideRows(o, grid) {
  const rows = grid?.rows ?? [];
  if (!o || !rows.length || !grid.rowMinutes) return null;
  const first = rows[0].time ?? new Date(rows[0].startsAt).getTime();
  const rowMs = grid.rowMinutes * 60000;
  const start = new Date(o.startsAt).getTime();
  const end = new Date(o.endsAt).getTime();
  if (Number.isNaN(start) || Number.isNaN(end)) return null;
  // X94 (R35 M1): the grid's own rows looked up (they are the API's slots, not first + n × step).
  const lastRow = rows[rows.length - 1];
  const gridEnd = lastRow.end ?? (lastRow.time ?? new Date(lastRow.startsAt).getTime()) + rowMs;
  if (end <= first || start >= gridEnd) return null;
  const row = start <= first ? 0 : gridRowAt(rows, start);
  const endRow = Math.min(rows.length, gridRowAt(rows, end, {end: true}));
  if (endRow <= row) return null;
  return {row, span: endRow - row, clippedStart: start < first, clippedEnd: end > gridEnd};
}

/**
 * The local "HH:mm" pair a row range names on the club's clock ("24:00" for an
 * end at the next midnight), for the dialog opened after a drag on the lane.
 */
export function rangeToLocal(rows, row, span, rowMinutes, timeZone, date) {
  const first = rows?.[row];
  if (!first) return {startLocal: null, endLocal: null};
  const start = first.time ?? new Date(first.startsAt).getTime();
  // X94 (R35 M1): the rows' own end (the next row's start, or the last row's end), as rowsToTimes reads it.
  const after = rows[row + span];
  const lastRow = rows[row + span - 1];
  const end = after ? (after.time ?? new Date(after.startsAt).getTime())
    : lastRow?.end != null && row + span === rows.length ? lastRow.end : start + span * rowMinutes * 60000;
  const startLocal = clockInZone(new Date(start).toISOString(), timeZone);
  let endLocal = clockInZone(new Date(end).toISOString(), timeZone);
  if (endLocal === '00:00' || (date && dayInZone(new Date(end).toISOString(), timeZone) !== date)) endLocal = '24:00';
  return {startLocal, endLocal};
}

/** Minutes snapped to the nearest multiple of the step (0 and 1440 stay). */
export function snapMinutes(minutes, step) {
  const s = Math.max(5, step || 5);
  return Math.max(0, Math.min(1440, Math.round(minutes / s) * s));
}

/**
 * The From / To choices of the dialog: every step from midnight, the To list
 * ending with midnight ("24:00"); a value from the drag or an existing change
 * that is off the step is added so the select can show it.
 */
export function overrideTimeChoices(step, {includeEnd = false, extra = null} = {}) {
  const s = Math.max(5, step || 30);
  const out = [];
  for (let m = 0; m < 1440; m += s) out.push({value: toHhmm(m), title: hhmmWord(toHhmm(m))});
  if (includeEnd) out.push({value: '24:00', title: 'midnight'});
  if (extra && !out.some(o => o.value === extra)) {
    out.push({value: extra, title: hhmmWord(extra)});
    out.sort((a, b) => minutesOf(a.value === '24:00' ? '23:59' : a.value) - minutesOf(b.value === '24:00' ? '23:59' : b.value) || (a.value === '24:00' ? 1 : 0));
  }
  return out;
}

/** The end in minutes, "24:00" being the end of the day. */
export function endMinutes(hhmm) {
  if (!hhmm) return 0;
  if (hhmm === '24:00') return 1440;
  const m = minutesOf(hhmm);
  return m === 0 ? 1440 : m;
}

/** Whether the range is a range: a start, an end after it, inside the day. */
export function overrideValidity({startLocal, endLocal}) {
  if (!startLocal || !endLocal) return {ok: false, reason: 'Pick the times.'};
  const s = minutesOf(startLocal);
  const e = endMinutes(endLocal);
  if (e <= s) return {ok: false, reason: 'The change must end after it starts.'};
  return {ok: true};
}

/** Another change of the same group on the same day that the range overlaps (the API refuses it; the dialog says so first). */
export function overlappingChange(list, {groupId, startLocal, endLocal, ignoreId = null}) {
  const s = minutesOf(startLocal);
  const e = endMinutes(endLocal);
  return (list ?? []).find(o => o.areaGroupId === groupId && o.id !== ignoreId && o.startMinute < e && o.endMinute > s) ?? null;
}

/** The request body (DayOverrideRequest). */
export function overrideBody({groupId, date, startLocal, endLocal, kind, note = '', cancelPlayBookings = false, keepClubBlocks = true, confirm = false}) {
  return {
    areaGroupId: groupId,
    date,
    startLocal,
    endLocal,
    kind,
    note: (note ?? '').trim() || null,
    cancelPlayBookings: !!cancelPlayBookings,
    // X234: the club's own blocks the change would cancel stay unless the admin picks Cancel them.
    keepClubBlocks: !!keepClubBlocks,
    confirm: !!confirm,
  };
}

/** The UTC instants of a "HH:mm" range on a date, for the ghost's label and the preview. */
export function overrideInstants(date, startLocal, endLocal, timeZone) {
  const endsNextDay = endLocal === '24:00';
  const endDate = endsNextDay ? shiftDay(date, 1) : date;
  return {startsAt: instantInZone(date, startLocal, timeZone), endsAt: instantInZone(endDate, endsNextDay ? '00:00' : endLocal, timeZone)};
}

function shiftDay(yyyyMmDd, days) {
  const [y, m, d] = String(yyyyMmDd).split('-').map(n => parseInt(n, 10));
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, '0')}-${String(t.getUTCDate()).padStart(2, '0')}`;
}

/** "1 day change coming up" / "3 day changes coming up" (null for none), the Bookings page's group row. */
export function dayChangesLine(n) {
  if (!n) return null;
  return `${n} day change${n === 1 ? '' : 's'} coming up`;
}

/** The calendar route query for the first change coming up on a group. */
export function dayChangesQuery(group) {
  const first = group?.upcomingDayOverrides?.[0];
  return first ? {date: first.date, group: group.id} : {group: group?.id};
}

/** X76 (R26 L7): "Playing areas'" / "Court 1's": the possessive of a name, a plural ending in s taking the bare apostrophe. */
export function possessive(name) {
  const n = String(name ?? '').trim();
  if (!n) return '';
  return /s$/i.test(n) ? `${n}'` : `${n}'s`;
}

/**
 * X76 (R26 L7): the Matches-only tick, one booking or several ("Cancel the 1 booking in this time and refund it"), with
 * no "in full" (part may already have gone back; the preview lists each refund).
 */
export function cancelChoiceLabel(count) {
  const n = count > 0 ? count : 0;
  if (!n) return 'Cancel the bookings in this time and refund them';
  return `Cancel the ${n} booking${n === 1 ? '' : 's'} in this time and refund ${n === 1 ? 'it' : 'them'}`;
}

export function cancelChoiceHelp(count) {
  const one = count === 1;
  return `Unticked, ${one ? 'it stays' : 'they stay'} and still ${one ? 'happens' : 'happen'}; only matches can be booked there from now on.`;
}

/** What the preview's choice reads as for a Matches only change: the play bookings it would keep. */
export function keptPlayCount(preview) {
  // X238 (TEST_R99 L-4): the players' bookings only (the API's playBookings, left out when none), never the club's own
  // blocks: those have their own Keep / Cancel choice, so the tick never counts or offers to refund them.
  return Number(preview?.playBookings) || 0;
}

/** The dialog's primary button: "Change hours" or, for a kind, "Make it matches only". */
export function overrideButtonText(kind, editing = false) {
  if (editing) return 'Save';
  switch (kind) {
    case OVERRIDE_KIND.matchesOnly: return 'Make it matches only';
    case OVERRIDE_KIND.openPlay: return 'Make it open play';
    case OVERRIDE_KIND.closed: return 'Close it';
    default: return 'Make it bookable';
  }
}

/**
 * X91 (Grant 2026-10-21: "drag in empty slots and then have an option (for admins) to freeze, close etc."): what the
 * calendar's choice after a drag (or a tap) across empty time offers, and its words. Book always (an admin may book
 * over a rule, with its chips); inside a freeze, change or end that freeze, else freeze the playing area; inside a day
 * change, change that, else change the hours for this time (a day change for every playing area of the booking rules,
 * named). {areaName, dayWord, period, groupName, groupAreaNames, chips, freezeWords, changeWords}
 *   → {title, line, choices: [{key: 'book' | 'freeze' | 'editFreeze' | 'hours' | 'editChange', icon, title, sub}]}
 */
export function selectionChoices({areaName, dayWord, period, groupName = 'these booking rules', groupAreaNames = [], chips = [], freezeWords = null, changeWords = null, multi = false, closedTime = false, reopenDay = false} = {}) {
  const names = groupAreaNames.length ? groupAreaNames.join(', ') : areaName;
  const choices = [
    // X197 (Grant 2026-10-27: on closed time the only choices were Book anyway or Freeze, "no way to open it"): closed
    // time leads with opening it (the change dialog with Booking needed picked, Matches only and Open play a tap away),
    // and a day a "Closed all day" change shuts goes back to its usual hours in one press.
    ...(closedTime ? [{key: 'openHours', icon: 'mdi-lock-open-variant-outline', title: 'Open these hours', sub: `This day only, for every playing area of ${groupName} (${names}): Booking needed, Matches only or Open play.`}] : []),
    ...(reopenDay ? [{key: 'reopenDay', icon: 'mdi-calendar-refresh', title: 'Reopen this day', sub: `Back to the usual hours for ${dayWord}.`}] : []),
    // X95 (R36 L5): what the Book dialog offers: a player or the club on one court, a club block on each of several.
    {key: 'book', icon: 'mdi-plus-circle-outline', title: 'Book', sub: `${multi ? 'A club block on each playing area' : 'For a player or the club'}${chips.length ? `, over the rules: ${chips.join(', ')}` : ''}.`},
    freezeWords
      ? {key: 'editFreeze', icon: 'mdi-snowflake', title: 'Change or end the freeze', sub: freezeWords}
      : {key: 'freeze', icon: 'mdi-snowflake', title: `Freeze ${areaName}`, sub: 'Closed for maintenance or an event for this time; bookings in it are moved or cancelled, as the dialog shows.'},
    changeWords
      ? {key: 'editChange', icon: 'mdi-calendar-edit', title: "Change this day's change", sub: changeWords}
      // X197: on closed time "Open these hours" is this same dialog (Booking needed picked), so it isn't offered twice.
      : (closedTime ? null : {key: 'hours', icon: 'mdi-calendar-edit', title: 'Change the hours for this time', sub: `This day only, for every playing area of ${groupName} (${names}): Closed, Matches only, Open play or Booking needed.`}),
  ].filter(Boolean);
  return {title: 'This time', line: `${areaName} · ${dayWord} · ${period}`, choices};
}

/**
 * X91 (Grant 2026-10-21: "Change hours for this day … should just be able to change the bookable/viewable hours for
 * that day"): the day changes that set one day's hours for one set of booking rules, through the existing day-change
 * API only. `current` is the day's hours as the calendar shows them ("HH:mm" pair), `wanted` the new pair, or
 * 'closed' (Closed all day) or 'usual' (back to the usual hours), `existing` the day's changes for the rules
 * ({id, startLocal, endLocal, kind}).
 *  - New hours: the time added at either end is a Booking needed change, the time taken off an end is an Open play
 *    change (nothing to book there, as outside the hours). A change that sits in time being added or taken off is
 *    replaced (listed in `dropped`, so the dialog confirms); the day's other changes inside the new hours stay (`kept`).
 *  - Closed all day: every change goes (`dropped`) and one Closed change covers the day.
 *  - Usual hours: every change goes.
 *   → {creates: [{startLocal, endLocal, kind}], deletes: [change], dropped: [change], kept: [change]}
 */
export function dayHoursPlan({current, wanted, existing = []}) {
  const list = existing ?? [];
  if (wanted === 'usual') return {creates: [], deletes: [...list], dropped: [...list], kept: []};
  if (wanted === 'closed') return {creates: [{startLocal: '00:00', endLocal: '24:00', kind: OVERRIDE_KIND.closed}], deletes: [...list], dropped: [...list], kept: []};
  const w0 = minutesOf(wanted.startLocal);
  const w1 = endMinutes(wanted.endLocal);
  // No hours on the day yet (open play all day): the hours are one Booking needed change.
  const c0 = current ? minutesOf(current.startLocal) : w1;
  const c1 = current ? endMinutes(current.endLocal) : w1;
  const parts = [];
  const part = (a, b, kind) => { if (b > a) parts.push({a, b, kind}); };
  part(w0, Math.min(c0, w1), OVERRIDE_KIND.bookingNeeded); // opens earlier
  part(Math.max(c1, w0), w1, OVERRIDE_KIND.bookingNeeded); // closes later
  part(c0, Math.min(w0, c1), OVERRIDE_KIND.openPlay); // opens later
  part(Math.max(w1, c0), c1, OVERRIDE_KIND.openPlay); // closes earlier
  const span = (o) => [minutesOf(o.startLocal), endMinutes(o.endLocal)];
  const touched = (o) => { const [s, e] = span(o); return parts.some(p => s < p.b && e > p.a); };
  const deletes = list.filter(touched);
  return {
    creates: parts.map(p => ({startLocal: toHhmm(p.a), endLocal: p.b >= 1440 ? '24:00' : toHhmm(p.b), kind: p.kind})),
    deletes, dropped: deletes, kept: list.filter(o => !touched(o)),
  };
}

/**
 * X94 (R35 L5): the day's changes that ARE this dialog's hours (what dayHoursPlan made): Closed all day, Booking needed
 * time at either end of the day's hours (opens earlier, closes later) and Open play right outside them (opens later,
 * closes earlier). The rest are the day's other changes ("This day also has …"); the dialog's own pieces are its hours,
 * not something else on the day.
 */
export function ownHoursPieces(current, existing = []) {
  const list = existing ?? [];
  const c0 = current ? minutesOf(current.startLocal) : null;
  const c1 = current ? endMinutes(current.endLocal) : null;
  return list.filter((o) => {
    const s = minutesOf(o.startLocal);
    const e = endMinutes(o.endLocal);
    if (o.kind === OVERRIDE_KIND.closed) return s === 0 && e === 1440;
    if (c0 == null) return false;
    if (o.kind === OVERRIDE_KIND.bookingNeeded) return s === c0 || e === c1;
    if (o.kind === OVERRIDE_KIND.openPlay) return e === c0 || s === c1;
    return false;
  });
}

/**
 * X94 (R35 H3): the one request that sets the day's hours (POST dayOverrides/replace and its preview): the changes the
 * plan deletes go and its creates come in the same save, so a new change may sit where a replaced one was.
 */
export function replaceBody(plan, {groupId, date, confirm = false, keepClubBlocks = true}) {
  return {
    areaGroupId: groupId,
    date,
    removeIds: (plan?.deletes ?? []).map((o) => o.id),
    changes: (plan?.creates ?? []).map((c) => ({startLocal: c.startLocal, endLocal: c.endLocal, kind: c.kind, note: null})),
    cancelPlayBookings: false,
    keepClubBlocks: !!keepClubBlocks,
    confirm: !!confirm,
  };
}

/** X91: the line every one-day change of the hours carries: "Changes Tue 22 Oct only. To change every week, edit the booking rules." */
export function onlyThisDayWords(date) {
  const d = date ? new Date(`${date}T12:00:00Z`) : null;
  const day = d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString('en-GB', {weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC'}).replace(',', '') : 'this day';
  return {lead: `Changes ${day} only.`, before: 'To change every week, ', link: 'edit the booking rules', text: `Changes ${day} only. To change every week, edit the booking rules.`};
}

/** X91: the From / To choices in the grid's 24-hour clock ("8:00", "24:00" for midnight as an end). */
export function hoursChoices(step, {includeEnd = false, extra = null} = {}) {
  return overrideTimeChoices(step, {includeEnd, extra}).map(o => ({value: o.value, title: clockWord(o.value, {end: o.value === '24:00'})}));
}

/**
 * X95 (R36 L3): whether the day-hours dialog asks for a second press ("Save anyway"): only when it has a reason to show,
 * a booking the change touches (`previews`) or another change it replaces (`droppedWords`, its own earlier pieces left
 * out). The dialog's own pieces are its hours and are replaced without asking.
 */
export function dayHoursNeedsConfirm({previews = [], droppedWords = []} = {}) {
  return (previews?.length ?? 0) > 0 || (droppedWords?.length ?? 0) > 0;
}

/** X197: a "Closed all day" change (Closed from 0:00 to 24:00), the one "Reopen this day" takes away. */
export function closesWholeDay(o) {
  return !!o && o.kind === OVERRIDE_KIND.closed && o.startLocal === '00:00' && ['24:00', '00:00'].includes(o.endLocal);
}

/**
 * X197 (Grant 2026-10-27, on the calendar's "Closed all day" card: "there is no obvious way to unclose it?"): the card's
 * words and actions. Closed by a day change: "Reopen this day" (back to the usual hours, at once) and "Change hours" (the
 * day hours dialog). Closed by the weekly hours: "Open this day" (the day hours dialog, open, to set this day's hours)
 * and a quiet "Change the weekly hours" (the weekly hours painter).
 */
export function closedDayCard({byChange = false} = {}) {
  return {
    title: 'Closed all day',
    body: 'Nobody can play or book on this day.',
    primary: byChange ? {key: 'reopen', label: 'Reopen this day', icon: 'mdi-calendar-refresh'} : {key: 'open', label: 'Open this day', icon: 'mdi-lock-open-variant-outline'},
    secondary: byChange ? {key: 'hours', label: 'Change hours', icon: 'mdi-clock-edit-outline'} : null,
    link: byChange ? null : {key: 'weekly', label: 'Change the weekly hours'},
  };
}

/** X197: the toast after "Reopen this day": "Back to the usual hours for Sat 31 Oct." */
export function reopenedWords(dayWord) {
  return `Back to the usual hours for ${dayWord || 'this day'}.`;
}

/**
 * X197: "Open these hours" on a selection inside a Closed day change: the change is cut around the new one (the API
 * refuses overlapping changes), in one replace. `carve` is the Closed change ({startLocal, endLocal, kind, note}),
 * `range` the new change's {startLocal, endLocal}. → the pieces of `carve` left before and after it.
 */
export function carvePieces(carve, range) {
  if (!carve || !range) return [];
  const c0 = minutesOf(carve.startLocal);
  const c1 = endMinutes(carve.endLocal);
  const r0 = minutesOf(range.startLocal);
  const r1 = endMinutes(range.endLocal);
  const out = [];
  const piece = (a, b) => { if (b > a) out.push({startLocal: toHhmm(a), endLocal: b >= 1440 ? '24:00' : toHhmm(b), kind: carve.kind, note: carve.note ?? null}); };
  piece(c0, Math.min(r0, c1));
  piece(Math.max(r1, c0), c1);
  return out;
}
